import { useEffect } from 'react';
import { dehydrate, type DehydratedState, QueryClient } from '@tanstack/react-query';
import { type GetServerSidePropsContext, type GetServerSidePropsResult, type NextPageWithLayout } from 'next';
import { useRouter } from 'next/router';
import { type QueryDataType } from '@/api/api.type';
import { getGetQuizUserInfoQueryKey, getQuizUserInfo } from '@/api/gen/quiz-user/quiz-user.gen';
import { getProductSidebarLayout } from '@/layout/ProductSidebarLayout';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { useUserFoldersQuery } from '@/shared/features/quiz-folder';
import { serverLogger } from '@/shared/logger/serverLogger';
import { withSsrLogging } from '@/shared/logger/withSsrLogging';
import { getDefaultServerSideProps } from '@/utils/next';
import { setPublicPageCache } from '@/utils/ssr';
import { readListSearchQuery } from '../utils/readListSearchQuery';
import { buildUserFolderVariables, USER_FOLDER_PAGE_SIZE } from '../utils/userFolderVariables';
import { UserFolderListPageView } from './UserFolderListPageView';
type UserFolderListPageProps = {
  userHashId: string;
  noIndex: true;
  dehydratedState: DehydratedState;
};

/**
 * `/profile/[userHashId]/folders` — 남의 프로필 공개 폴더 전체.
 *
 * 공개 정보라 프로필과 같이 SSR + 공개 캐시다(동적 라우트라 ISR 은 쓰지 않는다 — ID 마다 엔트리가 쌓인다).
 * 프로필 폴더 섹션과 내용이 겹치는 목록 페이지라 검색엔진에는 올리지 않는다.
 */
const UserFolderListPage: NextPageWithLayout<UserFolderListPageProps> = ({
  userHashId
}) => {
  const isInExternalPlatform = useIsInExternalPlatform();
  const router = useRouter();

  // 프로필과 같다 — 외부 교육 플랫폼 안에서는 남의 프로필을 열지 않는다.
  useEffect(() => {
    if (isInExternalPlatform) router.back();
  }, [isInExternalPlatform, router]);
  if (isInExternalPlatform) return null;
  return <UserFolderListPageView userHashId={userHashId} data-sentry-element="UserFolderListPageView" data-sentry-component="UserFolderListPage" data-sentry-source-file="index.page.tsx" />;
};
UserFolderListPage.getLayout = function (page) {
  return getProductSidebarLayout(page, 'p-0', {
    hideMobileBottomNavigation: true,
    hideContentPaddingWithoutSidebar: true
  });
};
export const getServerSideProps = withSsrLogging('user-profile-folders', async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<UserFolderListPageProps>> => {
  const userHashId = context.params?.userHashId as string ?? null;
  if (!userHashId) return {
    notFound: true
  };
  const queryClient = new QueryClient();
  const {
    searchText,
    order,
    period
  } = readListSearchQuery(context.query);
  try {
    const [{
      props: defaultProps
    }] = await Promise.all([getDefaultServerSideProps({
      ...context,
      namespaces: ['common']
    }), queryClient.prefetchQuery({
      queryKey: getGetQuizUserInfoQueryKey(userHashId),
      queryFn: () => getQuizUserInfo(userHashId)
    }), queryClient.prefetchInfiniteQuery(useUserFoldersQuery.getFetchOptions(buildUserFolderVariables({
      userId: userHashId,
      searchText,
      order,
      period,
      limit: USER_FOLDER_PAGE_SIZE
    })))]);
    const userRes = queryClient.getQueryData<QueryDataType<typeof getQuizUserInfo>>(getGetQuizUserInfoQueryKey(userHashId));
    if (!userRes?.data?.countryCode || !userRes.data.userType) {
      return {
        notFound: true
      };
    }
    // 캐시 헤더는 성공 데이터 검증 뒤에만 — notFound·catch 경로가 공유 캐시에 남지 않게(프로필과 같다).
    setPublicPageCache(context, {
      cacheable: true,
      profile: 'user'
    });
    return {
      props: {
        ...defaultProps,
        userHashId,
        noIndex: true,
        dehydratedState: dehydrate(queryClient)
      }
    };
  } catch (e) {
    serverLogger.error({
      err: e
    }, 'user-profile-folders ssr error');
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
});
export default UserFolderListPage;